// Version history for the composer's "History" button: versions newest first,
// grouped by day, a read-only preview, and Restore. A right-side sheet on
// desktop (list and preview side by side from 1024px), a bottom sheet on
// phones. Props are a contract with the composer: add optional ones, never
// remove or rename.
//
// Versions come from three places: useAutoSnapshot ('autosave', about every
// 10 minutes of writing), publishing ('publish'), "Save a version now"
// ('manual'); and restoring snapshots the current draft first ('restore').

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useBackend } from '../lib/session'
import { useMediaQuery, useNow } from '../lib/hooks'
import { sanitizeInlineHtml } from '../lib/sanitize'
import { headingLevel, isPinnedNote, quoteAttribution, thoughtSourceLabel, upgradePins } from '../lib/blocks'
import { formatDuration } from '../lib/audio'
import { calendarDaysAgo, clockTime, longDate, shortWeekdayDate } from '../lib/text'
import type { Block, VersionReason, Writing, WritingVersion } from '../lib/types'
import { btn, errorText, MobileSheet } from './ui'
import { useToast } from './Toast'
import { InlineConfirm } from './audio/InlineConfirm'
import { FramedImage } from './FramedImage'
import { LinkSite } from './LinkSite'
import { CaptionOverlay } from './CaptionOverlay'
import { imageFit } from '../lib/frame'
import { customWidth, isWrap } from '../lib/imageSize'
import { DEFAULT_SCENE_COLOR, SCENE_SHADE, sceneInk } from '../lib/sceneColors'

export interface HistoryPanelProps {
  writingId: string
  open: boolean
  onClose(): void
  /** Save pending edits before listing or restoring, so nothing is lost. */
  flush(): Promise<void>
  /** The composer swaps its document for the restored writing. */
  onRestored(w: Writing): void
}

const REASONS: Record<VersionReason, { label: string; cls: string }> = {
  publish: { label: 'Published', cls: 'bg-primary text-primary-foreground' },
  manual: { label: 'Saved', cls: 'bg-accent/15 text-accent' },
  autosave: { label: 'Autosave', cls: 'bg-muted text-muted-foreground' },
  restore: { label: 'Before restore', cls: 'bg-secondary text-foreground' },
}

function ReasonChip({ reason }: { reason: VersionReason }) {
  const r = REASONS[reason] ?? REASONS.autosave
  return (
    <span className={`whitespace-nowrap rounded-full px-[7px] py-px text-[10px] font-semibold tracking-[0.02em] ${r.cls}`}>{r.label}</span>
  )
}

function dayLabel(iso: string, now: Date): string {
  const d = calendarDaysAgo(iso, now)
  if (d === 0) return 'Today'
  if (d === 1) return 'Yesterday'
  const date = new Date(iso)
  return date.getFullYear() === now.getFullYear() ? shortWeekdayDate(date) : longDate(date)
}

export function HistoryPanel(props: HistoryPanelProps) {
  // Mounting per open means every visit lists fresh versions from a clean state.
  if (!props.open) return null
  return <HistoryDialog {...props} />
}

function HistoryDialog({ writingId, onClose: requestClose, flush, onRestored }: HistoryPanelProps) {
  const backend = useBackend()
  const toast = useToast()
  const phone = !useMediaQuery('(min-width: 640px)')
  const twoPane = useMediaQuery('(min-width: 1024px)')
  const now = useNow(60_000)
  const [versions, setVersions] = useState<WritingVersion[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState<'restore' | 'save' | null>(null)
  // A restore in flight can't be walked away from: its result replaces the
  // document, so closing waits until it lands (or fails).
  const restoring = useRef(false)
  restoring.current = busy === 'restore'
  const onClose = useCallback(() => {
    if (!restoring.current) requestClose()
  }, [requestClose])
  const [saveError, setSaveError] = useState<string | null>(null)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const flushRef = useRef(flush)
  flushRef.current = flush
  const alive = useRef(true)
  const closeRef = useRef<HTMLButtonElement>(null)

  const load = useCallback(
    async (withFlush = true): Promise<WritingVersion[] | null> => {
      setLoadError(null)
      try {
        // List what's saved even if the flush fails; restore is where it matters.
        if (withFlush) await flushRef.current().catch(() => {})
        const list = await backend.listVersions(writingId)
        if (alive.current) setVersions(list)
        return list
      } catch (e) {
        if (alive.current) setLoadError(errorText(e))
        return null
      }
    },
    [backend, writingId],
  )

  useEffect(() => {
    alive.current = true
    void load()
    return () => {
      alive.current = false
    }
  }, [load])

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    closeRef.current?.focus({ preventScroll: true })
    return () => previous?.focus?.({ preventScroll: true })
  }, [])

  useEffect(() => {
    if (phone) return // MobileSheet handles Escape itself
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [phone, onClose])

  const groups = useMemo(() => {
    const out: Array<{ label: string; items: WritingVersion[] }> = []
    for (const v of versions ?? []) {
      const label = dayLabel(v.created_at, now)
      const last = out[out.length - 1]
      if (last?.label === label) last.items.push(v)
      else out.push({ label, items: [v] })
    }
    return out
  }, [versions, now])

  // Side by side, something is always previewed (the newest by default).
  const selected = versions?.find((v) => v.id === selectedId) ?? (twoPane ? (versions?.[0] ?? null) : null)

  const select = (id: string | null) => {
    setSelectedId(id)
    setConfirming(false)
    setRestoreError(null)
  }

  const saveNow = async () => {
    setBusy('save')
    setSaveError(null)
    try {
      await flushRef.current()
      await backend.snapshotWriting(writingId, 'manual')
      await load(false)
      toast('Version saved')
    } catch (e) {
      if (alive.current) setSaveError(errorText(e))
    } finally {
      if (alive.current) setBusy(null)
    }
  }

  const restore = async (v: WritingVersion) => {
    setBusy('restore')
    setRestoreError(null)
    try {
      // The draft as it stands (unsaved edits included) is what gets kept as "Before restore".
      await flushRef.current()
      const w = await backend.restoreVersion(writingId, v.id)
      onRestored(w)
      toast(`Restored the version from ${longDate(v.created_at)}, ${clockTime(v.created_at)}`)
      requestClose()
    } catch (e) {
      if (!alive.current) return
      setRestoreError(errorText(e))
      setBusy(null)
    }
  }

  const list = (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-2.5 px-[18px] pb-3 pt-[18px]">
        <div className="flex items-center justify-between text-[13px] font-semibold text-foreground">
          <h2 className="m-0 text-[13px] font-semibold">
            History
            {versions && versions.length > 0 && (
              <span className="font-medium text-muted-foreground">
                {' '}
                · {versions.length} version{versions.length === 1 ? '' : 's'}
              </span>
            )}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close history"
            className="-mr-1.5 flex size-7 items-center justify-center rounded-full text-[18px] font-light leading-none text-muted-foreground hover:bg-foreground/5"
          >
            ×
          </button>
        </div>
        <button type="button" onClick={() => void saveNow()} disabled={busy !== null} className={`${btn.outline} self-start`}>
          {busy === 'save' ? 'Saving…' : 'Save a version now'}
        </button>
        {saveError && (
          <p role="alert" className="m-0 text-[13px] text-primary">
            {saveError}
          </p>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-2.5 pb-6">
        {loadError ? (
          <div role="alert" className="mx-2 flex flex-col items-start gap-2 rounded-2xl border border-card-edge bg-card/70 px-4 py-3 text-[13px] text-primary">
            {loadError}
            <button type="button" onClick={() => void load()} className={btn.outline}>
              Try again
            </button>
          </div>
        ) : versions === null ? (
          <p role="status" className="m-0 px-2 py-8 text-center font-display text-[14px] italic text-muted-foreground">
            Loading versions…
          </p>
        ) : versions.length === 0 ? (
          <div className="mx-2 flex flex-col gap-1.5 rounded-2xl border border-dashed border-border/60 px-4 py-5">
            <p className="m-0 font-display text-[16px] italic text-muted-foreground">No versions yet</p>
            <p className="m-0 text-[13px]/[1.55] text-muted-foreground">
              Burrow keeps a version about every 10 minutes while you write, and another each time you publish. Save one yourself
              whenever you reach a place you might want to come back to.
            </p>
          </div>
        ) : (
          groups.map((g) => (
            <section key={g.label} aria-label={g.label} className="flex flex-col gap-1">
              <h3 className="m-0 px-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{g.label}</h3>
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {g.items.map((v) => {
                  const on = selected?.id === v.id
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        aria-pressed={on}
                        onClick={() => select(v.id)}
                        className={`flex w-full flex-col gap-1 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                          on ? 'border-border/60 bg-card shadow-[0_1px_3px_rgba(45,27,30,.06)]' : 'border-transparent hover:bg-card/60'
                        }`}
                      >
                        <span className="flex items-center gap-2 text-[11.5px] font-medium text-muted-foreground">
                          <span className="tabular-nums">{clockTime(v.created_at)}</span>
                          <ReasonChip reason={v.reason} />
                        </span>
                        <span className="truncate font-display text-[15px]/[1.3] text-primary">{v.title.trim() || 'Untitled'}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {v.word_count.toLocaleString()} word{v.word_count === 1 ? '' : 's'}
                          {v.narration ? ' · narrated' : ''}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  )

  const preview = selected && (
    <div className="flex min-h-0 flex-1 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 border-b border-border/40 px-5 py-3 font-sans">
        {!twoPane && (
          <button type="button" onClick={() => select(null)} className={`${btn.ghost} -ml-2.5`}>
            ← All versions
          </button>
        )}
        <span className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-muted-foreground">
          <ReasonChip reason={selected.reason} />
          <span className="truncate">
            {longDate(selected.created_at)} · {clockTime(selected.created_at)}
          </span>
        </span>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={busy !== null || confirming}
          className={`${btn.primary} ml-auto`}
        >
          Restore this version
        </button>
      </div>
      {(confirming || restoreError) && (
        <div className="flex flex-col gap-2 px-5 pt-3">
          {confirming && (
            <InlineConfirm
              message="Restore this version?"
              detail="Your current draft is saved to History first."
              confirmLabel={busy === 'restore' ? 'Restoring…' : 'Restore'}
              busy={busy === 'restore'}
              onConfirm={() => void restore(selected)}
              onCancel={() => setConfirming(false)}
            />
          )}
          {restoreError && (
            <p role="alert" className="m-0 text-[13px] text-primary">
              {restoreError}
            </p>
          )}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-10 pt-6 sm:px-8">
        <VersionPreview version={selected} />
      </div>
    </div>
  )

  if (phone) {
    return (
      <MobileSheet label="History" onClose={onClose}>
        {selected ? preview : list}
      </MobileSheet>
    )
  }
  return (
    <div className="fixed inset-0 z-50">
      <button type="button" aria-label="Close" tabIndex={-1} onClick={onClose} className="absolute inset-0 bg-foreground/20" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="History"
        className={`absolute inset-y-0 right-0 flex max-w-full overflow-hidden border-l border-border/40 bg-background font-sans shadow-[var(--shadow-sheet)] ${
          twoPane ? 'w-[min(920px,calc(100vw-64px))]' : 'w-[440px]'
        }`}
      >
        {twoPane ? (
          <>
            <div className="flex w-[320px] shrink-0 flex-col border-r border-border/40">{list}</div>
            {preview || (
              <div className="flex flex-1 items-center justify-center bg-card px-8 text-center font-display text-[15px] italic text-muted-foreground">
                {versions?.length === 0 ? 'Nothing to preview yet.' : 'Pick a version to read it.'}
              </div>
            )}
          </>
        ) : selected ? (
          preview
        ) : (
          list
        )}
      </div>
    </div>
  )
}

/** A version, read-only: title, subtitle and the column text with its asides. Self-contained on purpose. */
function VersionPreview({ version }: { version: WritingVersion }) {
  const blocks = upgradePins(version.blocks ?? [])
  return (
    <article aria-label="Version preview" className="wr-prose mx-auto max-w-[560px] font-serif text-foreground">
      {version.cover_url && <FramedImage src={version.cover_url} frame={version.cover_frame} className="mb-5 h-[150px] w-full rounded-2xl" />}
      <h1 className="m-0 mb-2 font-display text-[28px]/[1.12] font-normal text-primary">{version.title.trim() || 'Untitled'}</h1>
      {version.subtitle.trim() && <p className="m-0 mb-3 text-[17px]/[1.35] font-light italic text-foreground/62">{version.subtitle}</p>}
      <p className="m-0 mb-6 border-b border-border/60 pb-4 font-sans text-[11.5px] font-medium text-muted-foreground">
        {version.word_count.toLocaleString()} word{version.word_count === 1 ? '' : 's'}
        {version.narration ? ` · narration ${formatDuration(version.narration.duration)}` : ''}
      </p>
      {blocks.some((b) => !isBlank(b)) ? (
        blocks.map((b) => <PreviewBlock key={b.id} block={b} />)
      ) : (
        <p className="m-0 italic text-muted-foreground">Nothing written in this version.</p>
      )}
    </article>
  )
}

function isBlank(b: Block): boolean {
  return (b.type === 'text' || b.type === 'heading' || b.type === 'pullquote') && !b.html.replace(/<[^>]*>|&nbsp;/g, '').trim()
}

const html = (s: string) => ({ __html: sanitizeInlineHtml(s) })

function Aside({ label, children }: { label: string; children: ReactNode }) {
  return (
    <aside className="my-3 ml-5 flex flex-col gap-1 rounded-2xl border border-card-edge bg-card px-3.5 py-3">
      <span className="font-sans text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
      {children}
    </aside>
  )
}

function PreviewBlock({ block: b }: { block: Block }) {
  if (isBlank(b)) return null
  switch (b.type) {
    case 'text':
      return <p className="m-0 mb-4 text-[16.5px]/[1.6]" dangerouslySetInnerHTML={html(b.html)} />
    case 'heading':
      return (
        <h2
          className={`m-0 mb-3 mt-7 font-display font-medium text-primary ${
            headingLevel(b) === 1 ? 'text-[25px]/[1.15]' : headingLevel(b) === 3 ? 'text-[18px]/[1.3] font-semibold' : 'text-[22px]/[1.2]'
          }`}
          dangerouslySetInnerHTML={html(b.html)}
        />
      )
    case 'pullquote': {
      const said = quoteAttribution(b)
      const cls = 'm-0 border-l-2 border-accent pl-4 text-[18px]/[1.5] italic'
      if (!said) return <blockquote className={`${cls} my-5`} dangerouslySetInnerHTML={html(b.html)} />
      return (
        <figure className="m-0 my-5">
          <blockquote className={cls} dangerouslySetInnerHTML={html(b.html)} />
          <figcaption className="mt-1.5 pl-[18px] font-sans text-[12px] font-medium text-muted-foreground">— {said}</figcaption>
        </figure>
      )
    }
    case 'thought':
      return (
        <figure className="m-0 my-5 rounded-2xl border border-card-edge bg-card px-4 py-3.5">
          <blockquote className="m-0 mb-1.5 text-[16px]/[1.5] italic">{b.text}</blockquote>
          <figcaption className="font-sans text-[11.5px] font-medium text-muted-foreground">{thoughtSourceLabel(b)}</figcaption>
        </figure>
      )
    case 'image': {
      const shape = imageFit(b.placement, b.frame) === 'shape'
      // Wraps show unfloated here, as they always have; a resized column image keeps its share of the column.
      const w = isWrap(b.placement) ? null : customWidth(b)
      return (
        <figure className="relative m-0 mx-auto my-5 flex flex-col" style={w ? { width: `${w}%` } : undefined}>
          <FramedImage
            src={b.src}
            alt={b.caption}
            frame={b.frame}
            fit={shape ? 'shape' : 'cover'}
            defaultPosition="center 60%"
            style={!shape && w ? { height: (220 * w) / 100 } : undefined}
            className={shape ? 'w-full rounded-[4px]' : 'h-[220px] w-full rounded-[4px]'}
          />
          <CaptionOverlay caption={b.caption} small />
        </figure>
      )
    }
    case 'linkcard':
      return (
        <div className="my-4 flex flex-col gap-1 rounded-2xl border border-card-edge bg-card px-4 py-3">
          <LinkSite url={b.url} />
          <span className="font-display text-[15px]/[1.35] text-primary">{b.title || b.url}</span>
        </div>
      )
    case 'collection':
      return (
        <div className="my-4 flex flex-col gap-1 rounded-2xl border border-card-edge bg-card px-4 py-3">
          <span className="font-sans text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground">COLLECTION</span>
          <span className="font-display text-[15px]/[1.35] text-primary">{b.name}</span>
        </div>
      )
    case 'divider':
      return <hr className="my-6 border-0 border-t border-border/80" />
    case 'margin':
      return (
        <Aside label={isPinnedNote(b) ? 'Pinned' : 'Margin note'}>
          <p className="m-0 text-[14px]/[1.45] italic">{b.text}</p>
        </Aside>
      )
    case 'unfold':
      return (
        <Aside label={`Unfolded · “${b.words}”`}>
          <p className="m-0 text-[14px]/[1.45] italic">{b.text}</p>
        </Aside>
      )
    case 'voice':
      return (
        <Aside label={`Voice memo · ${formatDuration(b.duration)}`}>
          {(b.transcript ?? '').trim() && <p className="m-0 text-[14px]/[1.45] italic text-muted-foreground">“{(b.transcript ?? '').trim()}”</p>}
        </Aside>
      )
    case 'scene':
      return (
        <div
          className="relative my-6 flex min-h-[120px] flex-col items-center justify-center overflow-hidden rounded-2xl px-5 py-5 text-center"
          style={
            b.mode === 'image' && b.src
              ? { color: 'var(--on-scrim)' }
              : { background: b.color || DEFAULT_SCENE_COLOR, color: sceneInk(b.color) }
          }
        >
          {b.mode === 'image' && b.src && (
            <>
              <FramedImage src={b.src} frame={b.frame} className="absolute inset-0" />
              <span aria-hidden className={SCENE_SHADE} />
            </>
          )}
          <h3 className="relative m-0 text-[22px]/[1.15] font-normal [text-wrap:balance]" dangerouslySetInnerHTML={html(b.html)} />
        </div>
      )
    case 'columns':
      // History reads the columns one after another, under a small label.
      return b.part === 'start' ? (
        <div className="my-5 border-t border-border/60 pt-1.5 font-sans text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Columns
        </div>
      ) : b.part === 'end' ? (
        <div aria-hidden className="my-5 border-t border-border/60" />
      ) : null
    default:
      // A block type this build doesn't know (saved by a newer one).
      return null
  }
}
